import { AudioStreamAnalyzer } from "./audio_stream_analyser.js";
import { EventBus } from "./event_bus.js";
import { Utils } from "./utils.js";
import { APP } from "./app.js";

// Owns the capture streams, the audio graph and the MediaRecorder.
//
// Lifetimes:
// - Mic: from the toggle switched on until it is switched off (or closeStreams()).
// - Screen: from the picker until it is changed, stops being shared, or the
//   session finishes. The test and the session share it, so Start does not ask again.
// - Full stream + MediaRecorder: one test recording or one session.
// - AudioContext: created on first use, closed by closeStreams().
//
// It never shows messages: every failure is a rejected Promise or a thrown
// error, and RecordingManager decides what to tell the tester.
export class SimpleRecorder {
  // VP8 costs a fraction of VP9 to encode in software and is still a standard
  // WebM (D2)
  static MIME_TYPES = [
    "video/webm;codecs=vp8",
    "video/webm;codecs=vp9",
    "video/webm",
  ];

  // Often hardware encoded, but a WebM only Chrome plays. Tried first only
  // with ?codec=h264 until the backend is known to handle it.
  static H264_MIME_TYPE = "video/webm;codecs=h264";

  // Readable text first, then frame rate (D1, D9): the test steps down to the
  // next profile when the encoder cannot keep up. Below 720p the text of a
  // 1440p screen cannot be read, so there is no lower step. The bitrate mostly
  // sets detail and upload size.
  static RECORDING_PROFILES = [
    { name: "1080p30", width: 1920, height: 1080, fps: 30, bitrate: 4_000_000 },
    { name: "720p30", width: 1280, height: 720, fps: 30, bitrate: 2_500_000 },
  ];

  static AUDIO_BITS_PER_SECOND = 96_000;

  // A refusal faster than this cannot be a person closing the screen picker:
  // the browser refused without showing it (C2)
  static BLOCKED_WITHOUT_PICKER_MS = 300;

  // getDisplayMedia only accepts ideal and max, never min or exact
  static videoConstraints(profile) {
    return {
      width: { ideal: profile.width },
      height: { ideal: profile.height, max: profile.height },
      frameRate: { ideal: profile.fps, max: profile.fps },
    };
  }

  // Hints for the screen picker (C5). Browsers ignore the ones they do not know.
  static displayMediaOptions(profile) {
    return {
      video: {
        displaySurface: "monitor",
        ...SimpleRecorder.videoConstraints(profile),
      },
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      selfBrowserSurface: "exclude",
      surfaceSwitching: "include",
      monitorTypeSurfaces: "include",
      systemAudio: "include",
    };
  }

  static profileByName(name) {
    return SimpleRecorder.RECORDING_PROFILES.find((profile) => profile.name === name) ?? null;
  }

  // Every WebM frame lives in a Cluster. The headers before the first one take
  // well under 1 KB, so a part over 4 KB always has one (A31).
  static CLUSTER_ID = [0x1f, 0x43, 0xb6, 0x75];
  static HEADERS_MAX_BYTES = 4096;

  // False only for a WebM part with no Cluster: a block stopped before its
  // first video frame. Chrome then writes the bare EBML header, which no
  // player or ffmpeg opens. Never throws; when unsure, the part has media (A31).
  static async hasMedia(blob) {
    if (blob.size === 0) {
      return false;
    }

    if (!blob.type.includes("webm") || blob.size > SimpleRecorder.HEADERS_MAX_BYTES) {
      return true;
    }

    try {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const [a, b, c, d] = SimpleRecorder.CLUSTER_ID;
      for (let i = 0; i + 3 < bytes.length; i++) {
        if (bytes[i] === a && bytes[i + 1] === b && bytes[i + 2] === c && bytes[i + 3] === d) {
          return true;
        }
      }
      return false;
    } catch (error) {
      return true;
    }
  }

  screenStream = null;
  micStream = null;
  fullStream = null;

  micAudioTrack = null;
  screenVideoTrack = null;
  screenAudioTrack = null;
  fullAudioTrack = null;

  audioCtx = null;
  audioDestination = null;
  micSource = null;
  micAnalyzer = null;
  screenAudioSource = null;
  screenAudioGain = null;
  silentOscillator = null;
  silentGain = null;

  mediaRecorder = null;
  mimeType = null;
  profile = SimpleRecorder.RECORDING_PROFILES[0];
  isMicActive = false;

  currentBlockPromise = null;
  stopTimeoutID = null;
  micStreamPromise = null;

  // Types whose recorder could not start here; the next candidate is used instead
  #rejectedMimeTypes = new Set();

  static error(name, message) {
    const error = new Error(message);
    error.name = name;
    return error;
  }

  static candidateMimeTypes() {
    if (Utils.getParamValue("codec") === "h264") {
      return [SimpleRecorder.H264_MIME_TYPE, ...SimpleRecorder.MIME_TYPES];
    }

    return SimpleRecorder.MIME_TYPES;
  }

  static supportedMimeType(rejected = new Set()) {
    if (typeof window.MediaRecorder !== "function") {
      return null;
    }

    return SimpleRecorder.candidateMimeTypes().find((mimeType) =>
      !rejected.has(mimeType) && MediaRecorder.isTypeSupported(mimeType)
    ) ?? null;
  }

  // What this browser lacks to record a session, empty when it can (C9).
  // Never throws.
  static missingCapabilities() {
    const missing = [];

    if (!navigator.mediaDevices?.getDisplayMedia) {
      missing.push("screen capture");
    }

    if (typeof window.MediaRecorder !== "function") {
      missing.push("MediaRecorder");
    } else if (SimpleRecorder.supportedMimeType() === null) {
      missing.push("WebM recording");
    }

    if (typeof window.AudioContext !== "function") {
      missing.push("AudioContext");
    }

    return missing;
  }

  // AudioContext :: INI
  getAudioContext() {
    if (this.audioCtx === null || this.audioCtx.state === "closed") {
      this.audioCtx = new AudioContext();
    }

    return this.audioCtx;
  }

  // A suspended AudioContext produces no audio frames, and MediaRecorder then
  // produces empty blobs. Make sure it runs before recording (A29).
  async prepareAudio() {
    const audioCtx = this.getAudioContext();

    if (audioCtx.state !== "running") {
      // resume() stays pending until the page gets a user gesture, so do not wait forever
      await Promise.race([
        audioCtx.resume(),
        new Promise((resolve) => setTimeout(resolve, 1000))
      ]);
    }

    if (audioCtx.state !== "running") {
      throw SimpleRecorder.error("AudioContextNotRunning", `The AudioContext is ${audioCtx.state}`);
    }
  }
  // AudioContext :: END

  // ScreenStream :: INI
  async initScreenStream() {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw SimpleRecorder.error("NotSupportedError", "getDisplayMedia is not supported");
    }

    this.closeScreen();

    const startedAt = performance.now();
    let screenStream;

    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia(SimpleRecorder.displayMediaOptions(this.profile));
    } catch (error) {
      // Chrome reports a closed picker and a blocked share with the same error
      if (error?.name === "NotAllowedError" && performance.now() - startedAt < SimpleRecorder.BLOCKED_WITHOUT_PICKER_MS) {
        error.withoutPicker = true;
      }

      throw error;
    }

    const screenVideoTrack = screenStream.getVideoTracks()[0];

    if (!screenVideoTrack) {
      screenStream.getTracks().forEach(track => track.stop());
      throw new Error("No Video Tracks in the screenStream");
    }

    // Keep the frame rate rather than the detail (D3). Mostly honoured by
    // WebRTC, cheap to set anyway.
    try {
      screenVideoTrack.contentHint = "motion";
    } catch (error) {
      console.warn("SimpleRecorder.initScreenStream() - contentHint", error);
    }

    this.screenStream = screenStream;
    this.screenVideoTrack = screenVideoTrack;
    this.screenAudioTrack = screenStream.getAudioTracks()[0] ?? null;

    if (this.screenAudioTrack === null) {
      APP.playcocolaAPI.sendDebugEvent("initScreenStream - No Audio Tracks in the screenStream");
    }

    // The browser "Stop sharing" button, or the shared window was closed (A2)
    screenVideoTrack.addEventListener("ended", () => {
      if (screenVideoTrack === this.screenVideoTrack) {
        EventBus.event("screen-share-ended");
      }
    }, { once: true });

    console.log("initScreenStream - Success");
  }

  isScreenLive() {
    return this.screenVideoTrack?.readyState === "live";
  }

  closeScreen() {
    this.releaseFullStream();

    this.screenStream?.getTracks().forEach(track => track.stop());

    this.screenStream = null;
    this.screenVideoTrack = null;
    this.screenAudioTrack = null;
  }
  // ScreenStream :: END

  // Profile :: INI
  // Used by the next MediaRecorder. A screen still shared is adjusted in
  // place, so the picker does not open again. Rejects when the browser
  // refuses the new constraints; the profile is kept anyway.
  async setProfile(profile) {
    this.profile = profile;

    if (this.isScreenLive()) {
      await this.screenVideoTrack.applyConstraints(SimpleRecorder.videoConstraints(profile));
    }
  }

  lowerProfile() {
    const profiles = SimpleRecorder.RECORDING_PROFILES;
    return profiles[profiles.indexOf(this.profile) + 1] ?? null;
  }
  // Profile :: END


  // MicStream :: INI
  initMicStream() {
    if (this.micStream !== null) {
      return Promise.resolve();
    }

    if (this.micStreamPromise === null) {
      this.micStreamPromise =
        this.#acquireMicStream()
          .finally(() => { this.micStreamPromise = null; });
    }

    return this.micStreamPromise;
  }

  async #acquireMicStream() {
    const micStream =
      await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true
      });

    const micAudioTrack = micStream.getAudioTracks()[0];

    if (!micAudioTrack) {
      micStream.getTracks().forEach(track => track.stop());
      throw new Error("No Audio Tracks in the micStream");
    }

    // Switched off while the browser was still asking for the mic
    if (!this.isMicActive) {
      micStream.getTracks().forEach(track => track.stop());
      return;
    }

    this.micStream = micStream;
    this.micAudioTrack = micAudioTrack;

    // Analyser available as soon as the mic exists, not only once recording (A6)
    const audioCtx = this.getAudioContext();
    this.micSource = audioCtx.createMediaStreamSource(micStream);
    this.micAnalyzer = new AudioStreamAnalyzer(audioCtx, this.micSource, 512);

    // The meter needs a running context, also before any recording
    if (audioCtx.state === "suspended") {
      audioCtx.resume().catch((error) => console.warn("AudioContext.resume()", error));
    }

    if (this.audioDestination !== null) {
      this.micSource.connect(this.audioDestination);
    }

    // Device unplugged (A2)
    micAudioTrack.addEventListener("ended", () => {
      if (micAudioTrack === this.micAudioTrack) {
        EventBus.event("mic-ended");
      }
    }, { once: true });

    console.log("initMicStream - Success");
  }

  async setMicActive(value) {
    this.isMicActive = value;

    if (!value) {
      this.releaseMic();
      return;
    }

    try {
      await this.initMicStream();
    } catch (error) {
      this.isMicActive = false;
      throw error;
    }
  }

  releaseMic() {
    this.micSource?.disconnect();
    this.micStream?.getTracks().forEach(track => track.stop());

    this.micStream = null;
    this.micAudioTrack = null;
    this.micSource = null;
    this.micAnalyzer = null;
  }
  // MicStream :: END

  // FullStream :: INI
  // Screen video + one mixed audio track (screen audio, mic and a silent
  // oscillator) for the MediaRecorder.
  getFullStream() {
    if (this.fullStream !== null) {
      return this.fullStream;
    }

    const audioCtx = this.getAudioContext();

    this.fullStream = new MediaStream();

    // Video Track
    if (this.screenVideoTrack !== null) {
      this.fullStream.addTrack(this.screenVideoTrack);
    } else {
      console.error("SimpleRecorder.getFullStream() - No Video Tracks in screenStream");
      APP.playcocolaAPI.sendDebugEvent("SimpleRecorder.getFullStream() - No Video Tracks in screenStream");
    }

    this.audioDestination = audioCtx.createMediaStreamDestination();
    this.fullAudioTrack = this.audioDestination.stream.getAudioTracks()[0];

    // Screen Audio, reduced so it does not cover the mic
    if (this.screenAudioTrack !== null) {
      this.screenAudioSource = audioCtx.createMediaStreamSource(this.screenStream);
      this.screenAudioGain = audioCtx.createGain();
      this.screenAudioGain.gain.value = 0.1;
      this.screenAudioSource.connect(this.screenAudioGain);
      this.screenAudioGain.connect(this.audioDestination);
    }

    // Mic Audio
    if (this.micSource !== null) {
      this.micSource.connect(this.audioDestination);
    }

    // Always keep the audio clock running, even if the mic or the screen audio
    // ends mid-session (A7). Workaround for https://bugs.chromium.org/p/chromium/issues/detail?id=1490888
    this.silentOscillator = audioCtx.createOscillator();
    this.silentGain = audioCtx.createGain();
    this.silentGain.gain.value = 0;
    this.silentOscillator.connect(this.silentGain);
    this.silentGain.connect(this.audioDestination);
    this.silentOscillator.start();

    this.fullStream.addTrack(this.fullAudioTrack);

    return this.fullStream;
  }

  releaseFullStream() {
    this.stop();
    this.mediaRecorder = null;

    if (this.audioDestination !== null) {
      try {
        this.micSource?.disconnect(this.audioDestination);
      } catch (error) {
        // was not connected
      }
    }

    this.silentOscillator?.stop();
    this.silentOscillator?.disconnect();
    this.silentGain?.disconnect();
    this.screenAudioSource?.disconnect();
    this.screenAudioGain?.disconnect();
    this.fullAudioTrack?.stop();

    this.silentOscillator = null;
    this.silentGain = null;
    this.screenAudioSource = null;
    this.screenAudioGain = null;
    this.audioDestination = null;
    this.fullAudioTrack = null;
    this.fullStream = null;
  }
  // FullStream :: END

  closeStreams() {
    console.log("SimpleRecorder.closeStreams()");

    this.closeScreen();
    this.releaseMic();

    if (this.audioCtx !== null && this.audioCtx.state !== "closed") {
      this.audioCtx.close().catch((error) => console.warn("AudioContext.close()", error));
    }
    this.audioCtx = null;
  }

  // MediaRecorder :: INI
  getMimeType() {
    if (this.mimeType !== null) {
      return this.mimeType;
    }

    if (typeof window.MediaRecorder !== "function") {
      throw SimpleRecorder.error("NotSupportedError", "MediaRecorder is not supported");
    }

    const mimeTypeSupported = SimpleRecorder.supportedMimeType(this.#rejectedMimeTypes);

    if (!mimeTypeSupported) {
      throw SimpleRecorder.error("NotSupportedError", "Not any mimeType supported");
    }

    this.mimeType = mimeTypeSupported;
    console.log("Using mimeType: " + this.mimeType);
    return this.mimeType;
  }

  // Falls back to the next mime type when a recorder cannot start with this
  // one. Returns false when there is none left to try.
  #rejectMimeType(mimeType, error) {
    this.#rejectedMimeTypes.add(mimeType);
    this.mimeType = null;

    const next = SimpleRecorder.supportedMimeType(this.#rejectedMimeTypes);
    APP.playcocolaAPI.sendDebugEvent(`SimpleRecorder: ${mimeType} failed (${Utils.normalizeError(error).name}), next: ${next}`);

    return next !== null;
  }

  getMediaRecorder() {
    if (this.mediaRecorder !== null) {
      return this.mediaRecorder;
    }

    const stream = this.getFullStream();

    for (;;) {
      const mimeType = this.getMimeType();

      try {
        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: this.profile.bitrate,
          audioBitsPerSecond: SimpleRecorder.AUDIO_BITS_PER_SECOND,
        });
        return this.mediaRecorder;
      } catch (error) {
        if (!this.#rejectMimeType(mimeType, error)) {
          throw error;
        }
      }
    }
  }

  resetMediaRecorder() {
    this.stop();
    this.mediaRecorder = null;
  }

  static #stopIfActive(mediaRecorder) {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
  }

  // API :: INI
  // Records one block. Resolves with the Blob when the block stops (timeout or
  // stop()), rejects on a recorder error. Never returns undefined (A1).
  record(seconds) {
    if (this.currentBlockPromise !== null) {
      return Promise.reject(new Error("SimpleRecorder.record() called while a block is still recording"));
    }

    let mediaRecorder;
    try {
      mediaRecorder = this.getMediaRecorder();
    } catch (error) {
      return Promise.reject(error);
    }

    const chunks = [];
    const mimeType = this.mimeType;
    let timeoutID = null;
    let finished = false;

    const block = new Promise((resolve, reject) => {
      // An error is followed by a stop event: settle only once
      const finish = (settle, value) => {
        if (finished) return;
        finished = true;
        clearTimeout(timeoutID);
        if (this.currentBlockPromise === block) {
          this.currentBlockPromise = null;
        }
        settle(value);
      };

      mediaRecorder.ondataavailable = (event) => {
        console.log("SimpleRecorder.onDataAvailable.size: ", event.data.size);
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        console.log("SimpleRecorder.onstop()");
        finish(resolve, new Blob(chunks, { type: mediaRecorder.mimeType || this.mimeType }));
      };

      mediaRecorder.onerror = (event) => {
        // H.264 is the experimental one: the next recorder uses VP8
        if (mimeType === SimpleRecorder.H264_MIME_TYPE) {
          this.#rejectMimeType(mimeType, event.error ?? event);
        }

        finish(reject, event.error ?? event); // A4
      };
    });

    try {
      mediaRecorder.start();
    } catch (error) {
      // The encoder for this type cannot start here: try the next type
      if (error?.name === "NotSupportedError" && mimeType !== null && this.#rejectMimeType(mimeType, error)) {
        this.mediaRecorder = null;
        return this.record(seconds);
      }

      return Promise.reject(error);
    }

    APP.playcocolaAPI.sendDebugEvent("SimpleRecorder.mediaRecorder.start()");

    // Bound to this recorder instance, so it can never stop a newer one (A3)
    timeoutID = setTimeout(() => {
      console.log("SimpleRecorder.timeout");
      SimpleRecorder.#stopIfActive(mediaRecorder);
    }, seconds * 1000);

    this.stopTimeoutID = timeoutID;
    this.currentBlockPromise = block;

    return block;
  }

  stop() {
    clearTimeout(this.stopTimeoutID);
    SimpleRecorder.#stopIfActive(this.mediaRecorder);
  }
  // API :: END
  // MediaRecorder :: END
}
